import React, { useState } from 'react';
import { PenLine, Check, X } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { CumChart, CLASS_COLORS } from './CumChart';
import { SlaBars } from './SlaBars';
import { BreakupKpiDrilldown } from '../BreakupKpiDrilldown';

const SplitLine = ({ split, classTargets, testId }) => !split ? null : (
  <p className="text-[10px] font-mono mt-0.5" data-testid={testId}>
    {['A', 'B', 'C'].map((c, i) => (
      <span key={c}>{i > 0 && <span className="text-muted-foreground"> · </span>}
        <span style={{ color: CLASS_COLORS[c] }}>{c} {split[c] ?? 0}{classTargets?.[c] != null ? <span className="text-muted-foreground">/{classTargets[c]}</span> : null}</span>
      </span>
    ))}
    {split.none > 0 && <span className="text-muted-foreground"> · ohne Klasse {split.none}</span>}
  </p>
);

const MainNumber = ({ card }) => {
  const k = card.kind;
  if (k === 'stock' && card.key === 'team_buffer') {
    return <><span className="text-3xl font-heading text-foreground">{card.weeks}</span><span className="text-sm text-muted-foreground ml-1">Wochen</span><span className="text-xs text-muted-foreground ml-2">({card.total} Briefe)</span></>;
  }
  if (k === 'stock') {
    return <><span className="text-3xl font-heading text-foreground">{card.total}</span>{card.target ? <span className="text-sm text-muted-foreground ml-1">/ ~{card.target}</span> : null}</>;
  }
  if (k === 'hours') {
    return <><span className="text-3xl font-heading text-foreground">{card.total ?? '—'}</span><span className="text-sm text-muted-foreground ml-1">h Median</span>{card.target ? <span className="text-xs text-muted-foreground ml-2">Ziel ≤ {card.target} h</span> : null}</>;
  }
  if (k === 'pct') {
    return <><span className="text-3xl font-heading text-foreground">{card.total} %</span>{card.target ? <span className="text-sm text-muted-foreground ml-1">/ {card.target} %</span> : null}</>;
  }
  if (k === 'ratio') {
    return <><span className="text-3xl font-heading text-foreground">{card.total}</span><span className="text-sm text-muted-foreground ml-1">von {card.target} fällig</span><span className="text-xs text-muted-foreground ml-2">({card.pct} %)</span></>;
  }
  return <><span className="text-3xl font-heading text-foreground">{card.total}</span>{card.target ? <span className="text-sm text-muted-foreground ml-1">/ {card.target}</span> : null}</>;
};

const BREAKUP_STAGE_SHORT = {
  collected: 'noch nicht freigegeben',
  approved: 'freigegeben, noch nicht in der Queue',
  in_queue: 'in der iAuto-Queue',
  written: 'geschrieben, noch nicht als versendet markiert',
};

// Break-up cards (Talu, 29.10.2026): where the still-open break-ups of the window stand.
const BreakupBreakdown = ({ card }) => {
  const b = card.breakdown;
  if (!b || b.carried_over + b.new_due === 0) return null;
  const stages = Object.entries(b.stages || {}).filter(([, n]) => n > 0);
  return (
    <p className="text-[10px] text-muted-foreground mt-0.5" data-testid={`kpi-breakups-breakdown-${card.key}`}>
      Offen: {b.carried_over > 0 && <span className="text-red-300">{b.carried_over} überfällig aus dem Vorfenster</span>}
      {b.carried_over > 0 && b.new_due > 0 && ' + '}
      {b.new_due > 0 && <span>{b.new_due} neu fällig bis {card.window?.deadline_label}</span>}
      {stages.map(([s, n]) => <span key={s}> · <span className={s === 'written' ? 'text-rose-300' : s === 'in_queue' ? 'text-purple-300' : ''}>{n} {BREAKUP_STAGE_SHORT[s]}</span></span>)}
    </p>
  );
};

const WindowTargetEditor = ({ card, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(card.target ?? '');
  if (!editing) {
    return <button onClick={() => { setVal(card.target ?? ''); setEditing(true); }} data-testid={`kpi-window-target-edit-${card.key}`}
      className="text-[9px] text-muted-foreground hover:text-sky-300 inline-flex items-center gap-0.5" title="Ziel nur für dieses Fenster ändern"><PenLine className="w-3 h-3" /> Ziel</button>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} data-testid={`kpi-window-target-input-${card.key}`}
        className="w-14 h-5 text-[10px] bg-secondary border border-border rounded-sm px-1 text-foreground" />
      <button onClick={async () => { await onSave(card, val); setEditing(false); }} data-testid={`kpi-window-target-save-${card.key}`} className="text-emerald-300"><Check className="w-3 h-3" /></button>
      <button onClick={() => setEditing(false)} className="text-muted-foreground"><X className="w-3 h-3" /></button>
    </span>
  );
};

export const KpiCard = ({ card, offset, isAdmin, onSetWindowTarget }) => {
  const win = card.window;
  const allowSplit = card.section !== 'talu';
  return (
    <div className={`border bg-card rounded-sm p-3 sm:p-4 min-w-0 ${card.primary ? 'border-sky-500/40' : 'border-border'}`} data-testid={`kpi-card-${card.key}`}>
      <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-2 mb-1">
        <div className="min-w-0">
          <h3 className="text-xs sm:text-sm font-heading text-foreground sm:truncate">{card.label}{card.primary && <span className="ml-1 text-[9px] font-mono text-sky-300 uppercase">Primär</span>}</h3>
          {win ? (
            <p className="text-[9px] font-mono text-muted-foreground" data-testid={`kpi-window-${card.key}`}>
              Deadline {win.deadline_label} · Fenster {win.label}{win.days > 7 ? ` (${win.days} Tage)` : ''}
            </p>
          ) : <p className="text-[9px] font-mono text-muted-foreground">laufend</p>}
        </div>
        <StatusBadge status={card.status} label={card.status_label} testId={`kpi-status-${card.key}`} />
      </div>
      <div className="flex items-baseline gap-1 flex-wrap" data-testid={`kpi-total-${card.key}`}>
        <MainNumber card={card} />
        {isAdmin && card.weekly_editable && onSetWindowTarget && <span className="ml-auto"><WindowTargetEditor card={card} onSave={onSetWindowTarget} /></span>}
      </div>
      {card.has_split && card.kind !== 'ratio' && <SplitLine split={card.split} classTargets={card.class_targets} testId={`kpi-split-${card.key}`} />}
      {card.subline && (card.kind === 'ratio' || !card.has_split || card.section === 'talu' || card.kind === 'stock') && (
        <p className="text-[10px] text-muted-foreground mt-0.5" data-testid={`kpi-subline-${card.key}`}>{card.subline}</p>
      )}
      {card.key.startsWith('team_breakups') && <BreakupBreakdown card={card} />}
      {card.rework_count > 0 && <p className="text-[10px] text-amber-300 mt-0.5" data-testid={`kpi-rework-${card.key}`}>davon {card.rework_count} nach der Deadline nachgearbeitet</p>}
      {card.by_author?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5" data-testid="kpi-letters-by-author">
          {card.by_author.map((a) => <span key={a.name} className="text-[10px] font-mono border border-border rounded-sm px-1.5 py-0.5 text-muted-foreground">{a.name} <span className="text-foreground">{a.count}</span></span>)}
        </div>
      )}
      {card.chart === 'cum' && <div className="mt-2"><CumChart card={card} allowSplit={allowSplit} /></div>}
      {card.chart === 'bars' && <div className="mt-2"><SlaBars card={card} /></div>}
      {card.key.startsWith('team_breakups') && <BreakupKpiDrilldown weekOffset={offset} kpi={card} />}
    </div>
  );
};
